import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FileText,
  LayoutDashboard,
  Mail,
  MapPin,
  PencilLine,
  Star,
  Trophy,
  UserRound,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Notification, {
  showToast,
} from "../../../components/common/notification/Notification";
import "./profile.css";

const Profile = () => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const userId = loggedInUser?.user?.id;
  const token = loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [userDetails, setUserDetails] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [assetUrl, setAssetUrl] = useState("");
  const [profilePicture, setProfilePicture] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchProfileRequest = async () => {
      if (!baseUrl || !token || !userId) {
        return null;
      }

      return axios.get(`${baseUrl}/admin/user/id/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    };

    const fetchProfile = async () => {
      if (!userId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const responseData = await fetchProfileRequest();
        const nextUser = responseData?.data?.user ?? null;
        const nextAssetUrl = responseData?.data?.assetUrl ?? "";
        const nextProfilePhoto = nextUser?.photo
          ? `${nextAssetUrl}${nextUser.photo}`
          : "";

        if (!isMounted) {
          return;
        }

        setUserDetails(nextUser);
        setAssetUrl(nextAssetUrl);
        setProfilePicture(nextProfilePhoto);
      } catch (error) {
        console.error("Error fetching profile:", error);
        if (isMounted) {
          showToast("Unable to load your profile right now.", {
            type: "error",
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const fetchSubscriptionDetails = async () => {
      if (!userId || !baseUrl) {
        setIsSubscriptionLoading(false);
        return;
      }

      setIsSubscriptionLoading(true);

      try {
        const response = await axios.get(
          `${baseUrl}/payments/subscriptions/user/${userId}`
        );

        if (isMounted) {
          setSubscriptionDetails(response?.data ?? null);
        }
      } catch (error) {
        console.error("Error fetching subscription details:", error);
        if (isMounted) {
          setSubscriptionDetails(null);
        }
      } finally {
        if (isMounted) {
          setIsSubscriptionLoading(false);
        }
      }
    };

    fetchProfile();
    fetchSubscriptionDetails();

    return () => {
      isMounted = false;
    };
  }, [baseUrl, token, userId]);

  useEffect(() => {
    if (!selectedFile) {
      return undefined;
    }

    const previewUrl = URL.createObjectURL(selectedFile);
    setProfilePicture(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [selectedFile]);

  const handleProfilePictureChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setSelectedFile(file);
  };

  const handleProfilePictureUpload = async () => {
    if (!selectedFile) {
      showToast("Please select a profile picture.", { type: "error" });
      return;
    }

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("file", selectedFile);

    try {
      setUploading(true);

      const response = await axios.put(
        `${baseUrl}/admin/changePicture`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response?.data?.statusType === "SUCCESS") {
        showToast("Profile picture uploaded successfully.", {
          type: "success",
        });
        setSelectedFile(null);

        const responseData = await axios.get(
          `${baseUrl}/admin/user/id/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const nextUser = responseData?.data?.user ?? null;
        const nextAssetUrl = responseData?.data?.assetUrl ?? "";
        const nextProfilePhoto = nextUser?.photo
          ? `${nextAssetUrl}${nextUser.photo}`
          : "";

        setUserDetails(nextUser);
        setAssetUrl(nextAssetUrl);
        setProfilePicture(nextProfilePhoto);
      } else {
        showToast("Something went wrong while uploading the picture.", {
          type: "error",
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      showToast("Upload failed. Please try again.", { type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const fullName = `${userDetails?.first_name || ""} ${userDetails?.last_name || ""
    }`.trim();
  const emailAddress = userDetails?.email_address || "Not provided";
  const displayName = fullName || (emailAddress !== "Not provided" ? emailAddress.split('@')[0] : "Admin");
  const userTypeLabel =
    userDetails?.userType?.name?.replaceAll("_", " ") ?? "Customer";
  const whatsappNumber = userDetails?.whatsapp_mobile_number || "Not provided";
  const cityName = userDetails?.cities?.[0]?.name || "Not selected";
  const stateName = userDetails?.cities?.[0]?.state?.name || "Not selected";
  const documents = userDetails?.documents ?? [];
  const isSubscriptionActive = Boolean(subscriptionDetails?.subscriptionPlanActive);

  const profileCompletion = useMemo(() => {
    const checkpoints = [
      Boolean(userDetails?.email_address),
      Boolean(userDetails?.whatsapp_mobile_number),
      Boolean(userDetails?.cities?.[0]?.name),
      documents.length > 0,
      Boolean(profilePicture),
    ];

    const completedCount = checkpoints.filter(Boolean).length;
    return Math.round((completedCount / checkpoints.length) * 100);
  }, [documents.length, profilePicture, userDetails]);

  const initials = getInitials(displayName);

  return (
    <>
      <Notification />

      <div className="cupf">
        <div className="cupf__header">
          <div className="cupf__title">
            <h1>Profile</h1>
            <p>Manage your account information and preferences</p>
          </div>
          <Link to="/user/edit-profile" className="cupf__edit-btn">
            <PencilLine size={16} />
            <span>Edit Profile</span>
          </Link>
        </div>

        <div className="cupf__grid">
          {/* Left Column: Personal Information */}
          <article className="cupf__card">
            <h2 className="cupf__card-title">Personal Information</h2>

            <div className="cupf__personal-grid">
              <div className="cupf__avatar-section">
                <div className="cupf__avatar-wrapper">
                  {profilePicture ? (
                    <img
                      src={profilePicture}
                      alt={`${displayName} profile`}
                      className="cupf__avatar-image"
                    />
                  ) : (
                    <div className="cupf__avatar-fallback" aria-hidden="true">
                      {initials}
                    </div>
                  )}
                  <label className="cupf__upload-trigger" htmlFor="cupf-profile-upload">
                    Change
                  </label>
                  <input
                    id="cupf-profile-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    className="cupf__file-input"
                  />
                </div>
                <div>
                  <div className="cupf__info-label">Full Name</div>
                  <div className="cupf__info-value">
                    <UserRound size={16} />
                    <span>{fullName || "Not provided"}</span>
                  </div>
                  {selectedFile && (
                    <button
                      type="button"
                      onClick={handleProfilePictureUpload}
                      disabled={uploading}
                      className="cupf__avatar-upload-btn"
                    >
                      {uploading ? "Uploading..." : "Save Picture"}
                    </button>
                  )}
                </div>
              </div>

              <div className="cupf__info-block">
                <div className="cupf__info-label">Email</div>
                <div className="cupf__info-value">
                  <Mail size={16} />
                  <span>{emailAddress}</span>
                </div>
              </div>

              <div className="cupf__info-block">
                <div className="cupf__info-label">Account Type</div>
                <div className="cupf__info-value">
                  <span>{userTypeLabel}</span>
                </div>
              </div>

              <div className="cupf__info-block">
                <div className="cupf__info-label">Username</div>
                <div className="cupf__info-value">
                  <span>{userDetails?.user_name || emailAddress.split('@')[0]}</span>
                </div>
              </div>

              <div className="cupf__info-block">
                <div className="cupf__info-label">WhatsApp</div>
                <div className="cupf__info-value">
                  <FaWhatsapp size={16} color="#1971C2" />
                  <span>{whatsappNumber}</span>
                </div>
              </div>

              <div className="cupf__info-block">
                <div className="cupf__info-label">Location</div>
                <div className="cupf__info-value">
                  <MapPin size={16} />
                  <span>{cityName !== "Not selected" ? `${cityName}, ${stateName}` : "Not selected"}</span>
                </div>
              </div>

              <div className="cupf__divider"></div>

              <div className="cupf__info-block cupf__bio-block">
                <div className="cupf__info-label">Bio</div>
                <div className="cupf__info-value">
                  <span>Not specified</span>
                </div>
              </div>
            </div>
          </article>

          {/* Right Column: Progress and Stats */}
          <aside className="cupf__sidebar">
            <article className="cupf__card cupf__progress-card">
              <h2 className="cupf__card-title">
                <Star size={20} />
                Profile Progress
              </h2>

              <div className="cupf__progress-header">
                <div className="cupf__progress-circle">
                  {initials}
                </div>
                <div className="cupf__progress-info">
                  <p>{userTypeLabel} </p>
                </div>
              </div>

              <div className="cupf__progress-bar-container">
                <div
                  className="cupf__progress-bar-fill"
                  style={{ width: `${profileCompletion}%` }}
                ></div>
              </div>

              <div className="cupf__progress-stats">
                <span>{profileCompletion} Points</span>
                <span>{100 - profileCompletion} to Complete</span>
              </div>
            </article>

            <article className="cupf__card">
              <h2 className="cupf__card-title">Statistics</h2>

              <div className="cupf__stat-row">
                <div className="cupf__stat-label">
                  <FileText size={18} />
                  <span>Documents Uploaded</span>
                </div>
                <div className="cupf__stat-value">
                  {documents.length > 0 ? (
                    <Link to="/user/upload-documents" className="cupf__doc-link">{documents.length}</Link>
                  ) : (
                    "0"
                  )}
                </div>
              </div>

              <div className="cupf__stat-row">
                <div className="cupf__stat-label">
                  <LayoutDashboard size={18} />
                  <span>Email Verified</span>
                </div>
                <div className="cupf__stat-value">
                  {userDetails?.email_verified ? "Yes" : "No"}
                </div>
              </div>

              <div className="cupf__stat-row">
                <div className="cupf__stat-label">
                  <Star size={18} />
                  <span>Subscription</span>
                </div>
                <div className="cupf__stat-value">
                  {isSubscriptionActive ? "Active" : "Inactive"}
                </div>
              </div>

              <div className="cupf__stat-row">
                <div className="cupf__stat-label">
                  <Trophy size={18} />
                  <span>Plan Duration</span>
                </div>
                <div className="cupf__stat-value">
                  {subscriptionDetails?.durationInDays ? `${subscriptionDetails.durationInDays} days` : "0 days"}
                </div>
              </div>
            </article>
          </aside>
        </div>
      </div>
    </>
  );
};

const getInitials = (name) => {
  const trimmedName = name?.trim();

  if (!trimmedName) {
    return "CU";
  }

  const segments = trimmedName.split(/\s+/).filter(Boolean);

  if (segments.length === 1) {
    return segments[0].slice(0, 2).toUpperCase();
  }

  return `${segments[0][0]}${segments[1][0]}`.toUpperCase();
};

export default Profile;
